import { isAfter, isBefore, parseISO } from 'date-fns'

import type { DDELicensePayload, DDELicenseResponse, Season } from '@img-arena/consoles-api-dde'
import { ddeApi } from '@/service/ddeApi'
import getResponseErrorMessage from '@/utils/_legacy/getResponseErrorMessage'
import { getLicensePropertySeasonsEndpoint } from '@/utils/licenseUtils'
import { notifyError } from '@/utils/notifyError'
import { LICENSE_SUBSCRIPTION_STATUSES } from '@/views/Operators/Operator/Licenses/licenses.dde.table'

type LicenseSport = { sport: string; startDate: string; endDate: string }
type LicenseSportsMap = Record<string, LicenseSport>

/**
 * Builds a map of sports to their overall licensing periods from a list of licenses.
 * For each sport, finds the earliest start date and latest end date across all licensing periods.
 *
 * @param {License[]} data - Array of License objects to process.
 * @returns {LicenseSportsMap} Map of sport names to their LicenseSport info.
 */
function buildSportsMap(data: DDELicensePayload[]): LicenseSportsMap {
  const sportsMap: LicenseSportsMap = {}
  data.forEach(({ licensingPeriods, property }) => {
    const sport = getLicensePropertySeasonsEndpoint(property)
    if (!sport || !Array.isArray(licensingPeriods)) return
    licensingPeriods.forEach((period) => {
      const { startDate, endDate } = period || {}
      if (!startDate || !endDate) return
      if (!sportsMap[sport]) {
        sportsMap[sport] = { sport, startDate, endDate }
      } else {
        if (isBefore(parseISO(startDate), parseISO(sportsMap[sport].startDate))) {
          sportsMap[sport].startDate = startDate
        }
        if (isAfter(parseISO(endDate), parseISO(sportsMap[sport].endDate))) {
          sportsMap[sport].endDate = endDate
        }
      }
    })
  })
  return sportsMap
}

/**
 * Fetches seasons for each sport within the given licensing periods.
 *
 * @param {LicenseSport[]} sports - Array of LicenseSport objects to fetch seasons for.
 * @param {AbortSignal} signal - AbortSignal to cancel the requests if needed.
 * @returns {Promise<Record<string, Season[]>>} A promise resolving to a map of sport names to their fetched seasons.
 */
async function fetchSportSeasons(
  sports: LicenseSport[],
  signal: AbortSignal
): Promise<Record<string, Season[]>> {
  const sportSeasonsResponses = await Promise.all(
    sports.map(async ({ sport, startDate, endDate }) => {
      try {
        // TODO: add params dateFromWithinRange and dateToWithinRange instead of dateFrom and dateTo to also fetch seasons that are partially within the licensing period dates.
        // At the moment, it only fetches seasons that are FULLY within the licensing period dates.
        // Fran from DDE mentioned that they will add this query param in a couple of weeks.
        // Slack conversation: https://imgarena.slack.com/archives/CSL9J2NCQ/p1751554850916639
        // DDE ticket: https://imgarenahub.atlassian.net/browse/DDEJ-1268
        // For now, we can batch fetch all seasons and filter them on the frontend (too much data) or just return what we have (can miss some seasons but generally the season is >=1 full year so not too bad).
        const { data } = await ddeApi.endpoints.seasons.getAll(
          sport,
          {
            dateFrom: startDate,
            dateTo: endDate,
            page: 0,
            size: 100,
            type: 'Official'
          },
          signal
        )
        return { sport, seasons: Array.isArray(data?.content) ? data.content : [] }
      } catch (err) {
        notifyError(`Failed to fetch seasons for sport ${sport}: ${getResponseErrorMessage(err)}`)
        return { sport, seasons: [] }
      }
    })
  )
  const sportSeasonsMap: Record<string, Season[]> = {}
  sportSeasonsResponses.forEach(({ sport, seasons }) => {
    sportSeasonsMap[sport] = seasons
  })
  return sportSeasonsMap
}

/**
 * Maps each license's licensing periods to include the relevant seasons based on the sport and subscription type.
 *
 * @param {License[]} data - Array of License objects to update.
 * @param {Record<string, Season[]>} sportSeasonsMap - Map of sport names to their available seasons.
 * @returns {License[]} Array of licenses with updated licensing periods including seasons.
 */
function mapLicensesWithSeasons(
  data: DDELicenseResponse[],
  sportSeasonsMap: Record<string, Season[]>
): DDELicenseResponse[] {
  return data.map((license) => {
    const { licensingPeriods, property } = license
    const sport = getLicensePropertySeasonsEndpoint(property)
    if (!sport || !Array.isArray(licensingPeriods)) return license

    const updatedLicensingPeriods = licensingPeriods.map((period) => {
      if (!period) return period
      const { subscription } = period
      if (!subscription || !Array.isArray(subscription.competitionIds)) {
        return period
      }

      let type = subscription.type
      let seasons: Season[] = []
      const { competitionIds } = subscription
      const sportSeasons = sportSeasonsMap[sport] || []

      if (!type && competitionIds.length > 0) {
        // No subscription type but has competitionIds is treated as partial
        type = LICENSE_SUBSCRIPTION_STATUSES.COMPETITIONS_PARTIAL
      }

      if (type === LICENSE_SUBSCRIPTION_STATUSES.COMPETITIONS_PARTIAL) {
        seasons = sportSeasons.filter((season: Season) => {
          const seasonCompId =
            season.competition && typeof season.competition.id === 'string'
              ? season.competition.id
              : undefined
          return !!seasonCompId && competitionIds.includes(seasonCompId)
        })
      } else if (type === LICENSE_SUBSCRIPTION_STATUSES.COMPETITIONS_ALL) {
        seasons = sportSeasons
      } else {
        return period
      }

      return {
        ...period,
        subscription: {
          ...subscription,
          type: type,
          seasons
        }
      }
    })

    return {
      ...license,
      licensingPeriods: updatedLicensingPeriods
    }
  })
}

export { buildSportsMap, fetchSportSeasons, mapLicensesWithSeasons }
